import type {
  Dataset, Account, Contact,
  ActionItem, FinancialPeriod, EntityKind, ID, PnlGroup,
} from '@/types/entities'

/* ---- id lookups ---- */
export const byId = <T extends { id: string }>(arr: T[]) => {
  const m = new Map<string, T>()
  for (const x of arr) m.set(x.id, x)
  return m
}
export const accountById = (d: Dataset, id?: ID) => d.accounts.find((a) => a.id === id)
export const contactById = (d: Dataset, id?: ID) => d.contacts.find((c) => c.id === id)
export const propertyById = (d: Dataset, id?: ID) => d.properties.find((p) => p.id === id)
export const trackById = (d: Dataset, id?: ID) => d.tracks.find((t) => t.id === id)
export const fundById = (d: Dataset, id?: ID) => d.funds.find((f) => f.id === id)
export const opportunityById = (d: Dataset, id?: ID) => d.opportunities.find((o) => o.id === id)

export const contactName = (c?: Contact) => (c ? `${c.firstName} ${c.lastName}`.trim() : '—')

/** "Marc & Pam Laulhere" — the household label.
 *
 *  Deliberately NOT `contactName`: the contact record is Marc, and the
 *  contacts list, pickers and search should keep saying Marc. This is for the
 *  surfaces where the unit really is the couple — the symposium invitation
 *  list, where one invitation covers both of them. A spouse carrying a
 *  different surname keeps it rather than being renamed. */
export const coupleName = (c?: Contact): string => {
  if (!c) return '—'
  const spouse = (c.spouse ?? '').trim()
  const solo = contactName(c)
  if (!spouse || !(c.firstName ?? '').trim()) return solo
  const [spouseFirst, ...rest] = spouse.split(/\s+/)
  const spouseLast = rest.join(' ')
  const last = (c.lastName ?? '').trim()
  if (!spouseLast || spouseLast.toLowerCase() === last.toLowerCase()) {
    return `${c.firstName} & ${spouseFirst}${last ? ` ${last}` : ''}`.trim()
  }
  return `${solo} & ${spouse}`
}

/* ---- by-account collections ---- */
export const tracksForAccount = (d: Dataset, accountId: ID) =>
  d.tracks.filter((t) => t.accountId === accountId)
/** WPI staff. Two markers because both exist in the data: `isWpi` is set by
 *  the Entra directory sync, `type` by the seed and by hand-entered cards. */
export const isInternalContact = (c: Contact) =>
  c.isWpi === true || c.type === 'WPI Employee'

/** An account's COUNTERPARTY contacts. WPI staff are excluded on purpose: a
 *  colleague filed against a client account reads as that client's employee,
 *  which is how Jorge Saca, Brett Potomac and Troy Bergen came to be shown as
 *  Heitman's people (they are ours, and the pairing came from prototype seed
 *  data, not from any system of record). Use `allContactsForAccount` for the
 *  internal account, whose whole population IS staff. */
export const contactsForAccount = (d: Dataset, accountId: ID) =>
  d.contacts.filter((c) => c.accountId === accountId && !c.archived && !isInternalContact(c))

/** The WPI-internal account — the one place staff SHOULD be listed. Matched
 *  by id and by name because both forms exist: the seed slug `acct-wpi` and
 *  the synced row "Westport Properties (Internal)" (a uuid in every live
 *  environment). Account `type` is NOT a usable test — real households are
 *  'Personal' too. */
export const isInternalAccount = (a?: Account) =>
  !!a && (a.id === 'acct-wpi' || /westport properties \(internal\)/i.test(a.name))

/** Everything filed under an account, staff included — the internal account. */
export const allContactsForAccount = (d: Dataset, accountId: ID) =>
  d.contacts.filter((c) => c.accountId === accountId && !c.archived)
export const propertiesForAccount = (d: Dataset, accountId: ID) =>
  d.properties.filter((p) => p.ownerAccountId === accountId)
export const opportunitiesForAccount = (d: Dataset, accountId: ID) =>
  d.opportunities.filter((o) => o.principalAccountId === accountId)
export const fundsForAccount = (d: Dataset, accountId: ID) =>
  d.funds.filter((f) => f.investorAccountIds.includes(accountId))

/* ---- polymorphic attachments ---- */
export const matchesRef = (kind: EntityKind, id: ID) => (ref: { kind: EntityKind; id: ID }) =>
  ref.kind === kind && ref.id === id

export const actionItemsForRef = (d: Dataset, kind: EntityKind, id: ID) =>
  d.actionItems.filter((a) => a.attachedTo.kind === kind && a.attachedTo.id === id)

/** Tasks a property should surface: attached directly, OR an account-level
 *  task that names this property in relatedPropertyIds (one commitment,
 *  visible on every property it concerns). */
export const actionItemsForProperty = (d: Dataset, propertyId: ID) =>
  d.actionItems.filter((a) =>
    (a.attachedTo.kind === 'property' && a.attachedTo.id === propertyId)
    || (a.relatedPropertyIds?.includes(propertyId) ?? false))

export const activitiesForRef = (d: Dataset, kind: EntityKind, id: ID) =>
  d.activities.filter((a) => a.attachedTo.some(matchesRef(kind, id)))

export const financialPeriodsForProperty = (d: Dataset, propertyId: ID) =>
  d.financialPeriods.filter((fp) => fp.propertyId === propertyId)

export const workOrdersForProperty = (d: Dataset, propertyId: ID) =>
  d.workOrders.filter((w) => w.propertyId === propertyId)

/* ---- account-scoped open action items (rolls up via attached property/track/account) ---- */
export function openItemsForAccount(d: Dataset, accountId: ID): ActionItem[] {
  const propIds = new Set(d.properties.filter((p) => p.ownerAccountId === accountId).map((p) => p.id))
  const trackIds = new Set(d.tracks.filter((t) => t.accountId === accountId).map((t) => t.id))
  return d.actionItems.filter((a) => {
    if (a.status === 'Closed') return false
    const r = a.attachedTo
    if (r.kind === 'account' && r.id === accountId) return true
    if (r.kind === 'property' && propIds.has(r.id)) return true
    if (r.kind === 'track' && trackIds.has(r.id)) return true
    return false
  })
}

/* ---- P&L math ---- */
export const sumGroup = (fp: FinancialPeriod, group: PnlGroup, field: 'actual' | 'budget') =>
  fp.lines.filter((l) => l.group === group).reduce((s, l) => s + (l[field] || 0), 0)

export function noi(fp: FinancialPeriod, field: 'actual' | 'budget'): number {
  const rev = sumGroup(fp, 'revenue', field)
  const ctrl = sumGroup(fp, 'controllable', field)
  const unctrl = sumGroup(fp, 'uncontrollable', field)
  return rev - ctrl - unctrl
}

/* ---- snapshots ---- */
export const latest = <T>(snaps?: { date: string; value: T }[]): T | undefined =>
  snaps && snaps.length ? [...snaps].sort((a, b) => (a.date < b.date ? 1 : -1))[0].value : undefined

/* ---- account rollup (used by list + home cards when metrics not pre-baked) ---- */
export function rollupAccount(d: Dataset, account: Account) {
  const props = propertiesForAccount(d, account.id)
  const owned = props.filter((p) => p.relationshipType === 'Owned').length
  const managed = props.filter((p) => p.relationshipType === 'Managed-3PM').length
  const stabilized = props.filter((p) => p.stabilization === 'Stabilized').length
  const openItems = openItemsForAccount(d, account.id).length
  return {
    propertyCount: props.length,
    owned,
    managed,
    stabilized,
    openItems,
    ...account.metrics,
  }
}
